import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSeo } from "@/lib/seo";

const Section = ({ n, title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-bold tracking-tight text-ink">{n}. {title}</h2>
    <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-1.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand" /><span>{t}</span></li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  useSeo({ title: "Privacy Policy · Better Days", description: "How Better Days collects, uses and protects your health and account data.", path: "/privacy-policy" });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-surface">
      <header className="border-b border-line bg-card/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3.5">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/better-days-mark.png" alt="Better Days" className="h-6 w-auto" />
            <span className="text-[15px] font-bold tracking-tight text-ink">Better Days</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-ink" data-testid="privacy-back">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12" data-testid="privacy-policy-page">
        <h1 className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">Privacy Policy — Better Days</h1>
        <p className="mt-3 text-sm text-muted">Last updated: 01 August 2026</p>
        <p className="mt-6 text-sm leading-relaxed text-muted">
          Better Days (“we”, “us”, “our”) is a digital wellness platform designed to help users track their
          health, build better habits, and receive personalised insights through Clara, our AI wellness
          assistant. We are committed to protecting your privacy and handling your data responsibly.
        </p>

        <Section n="1" title="Information We Collect">
          <p className="font-semibold text-ink">a. Information you provide</p>
          <Bullets items={[
            "Account details (name, email address)",
            "Wellness inputs (workouts, habits, metrics, notes)",
            "Subscription and payment information (processed securely by third‑party providers)",
          ]} />
          <p className="pt-2 font-semibold text-ink">b. Automatically collected information</p>
          <Bullets items={[
            "Device information (model, OS version)",
            "App usage data (features used, interactions, performance logs)",
            "Analytics data to improve app stability and user experience",
          ]} />
          <p className="pt-2 font-semibold text-ink">c. AI‑related data</p>
          <p>Clara processes your inputs to generate personalised insights. These insights are created automatically and stored securely within your account.</p>
        </Section>

        <Section n="2" title="How We Use Your Information">
          <p>We use your data to:</p>
          <Bullets items={[
            "Provide core app features",
            "Generate personalised insights and weekly summaries",
            "Improve app performance and user experience",
            "Deliver customer support",
            "Process payments and manage subscriptions",
            "Maintain security and prevent misuse",
          ]} />
          <p className="font-semibold text-ink">We do not sell your data.</p>
        </Section>

        <Section n="3" title="How Your Data Is Stored">
          <p>Your data is stored securely using industry‑standard encryption. We use trusted third‑party services for hosting, analytics, and payment processing. These providers comply with relevant data protection regulations.</p>
        </Section>

        <Section n="4" title="Sharing Your Information">
          <p>We only share your data with:</p>
          <Bullets items={[
            "Payment processors (for subscriptions)",
            "Analytics providers (to improve the app)",
            "Cloud hosting services (to store your data securely)",
          ]} />
          <p>We do not share your data with advertisers or unrelated third parties.</p>
        </Section>

        <Section n="5" title="Data Retention">
          <p>We retain personal data only for as long as necessary to provide the Better Days app’s features and services.</p>
          <Bullets items={[
            "Account information (email, profile data): Stored until the user deletes their account or requests deletion.",
            "Wellness inputs (workouts, habits, notes): Stored until the user deletes their account or manually deletes the data inside the app.",
            "Device and analytics data: Retained for up to 24 months for app performance, diagnostics, and security, after which it is automatically deleted or anonymised.",
            "Payment data: Stored by Stripe according to their own retention schedule; Better Days does not store payment card details.",
            "Backups: Encrypted backups are retained for 30 days before being overwritten.",
          ]} />
        </Section>

        <Section n="6" title="Your Rights">
          <p>You can:</p>
          <Bullets items={[
            "Access your data",
            "Update your information",
            "Request deletion of your account",
            "Withdraw consent for data processing",
            "Contact us for any privacy‑related questions",
          ]} />
          <p>To request changes or deletion, email us at <a href="mailto:support@betterdays.fit" className="font-medium text-brand hover:underline">support@betterdays.fit</a>.</p>
        </Section>

        <Section n="7" title="Cookies & Tracking">
          <p>Our website may use cookies to:</p>
          <Bullets items={["Improve performance", "Analyse traffic", "Remember your preferences"]} />
          <p>You can disable cookies in your browser settings.</p>
        </Section>

        <Section n="8" title="Children’s Privacy">
          <p>Better Days is not intended for users under 18. We do not knowingly collect data from children. We do not permit use of the app by children or minors.</p>
        </Section>

        <Section n="9" title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. The latest version will always be available on our website.</p>
        </Section>

        <Section n="10" title="Contact Us">
          <p>If you have questions about this Privacy Policy or how your data is handled, or, if you wish to request your data is removed, contact:</p>
          <p>Better Days Support Email: <a href="mailto:support@betterdays.fit" className="font-medium text-brand hover:underline">support@betterdays.fit</a></p>
        </Section>

        <div className="mt-12 border-t border-line pt-6">
          <Link to="/" className="text-sm font-semibold text-brand hover:underline">← Back to Better Days</Link>
        </div>
      </main>
    </div>
  );
}
